// ============================================================
// LinuxSSH.jsx
//
// A branded, responsive course content page for the
// "SSH & Remote Access" lesson.
//
// Author: ProDev Linux Series
// Features: Secure Shell usage, key-based authentication,
//           and hardening best practices for junior DevOps.
// ============================================================

import React, { useState, useEffect } from "react";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Collapse,
    Paper,
    Fab,
    Zoom,
    Stack
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import VpnKeyIcon             from "@mui/icons-material/VpnKey";
import RouterIcon             from "@mui/icons-material/Router";
import SecurityIcon           from "@mui/icons-material/Security";
import SettingsEthernetIcon   from "@mui/icons-material/SettingsEthernet";
import TerminalIcon           from "@mui/icons-material/Terminal";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LinkIcon               from "@mui/icons-material/Link";
import ExpandMoreIcon         from "@mui/icons-material/ExpandMore";
import ExpandLessIcon         from "@mui/icons-material/ExpandLess";
import ContentCopyIcon        from "@mui/icons-material/ContentCopy";
import DoneIcon               from "@mui/icons-material/Done";
import SchoolIcon             from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ShieldIcon             from "@mui/icons-material/Shield";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function ConceptCard({ icon, title, description }) {
    return (
        <Paper elevation={0} sx={{
            p: 2.5, height: "100%", background: BRAND.surface,
            border: `1px solid ${BRAND.border}`, borderRadius: 3,
            transition: "0.25s", "&:hover": { borderColor: BRAND.borderHover, boxShadow: `0 0 20px ${BRAND.goldGlow}` },
        }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.2 }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 22 } })}
                <Typography sx={{ fontFamily: "'Georgia', serif", fontWeight: 700, color: BRAND.white, fontSize: 14 }}>
                    {title}
                </Typography>
            </Box>
            <Typography sx={{ fontSize: 13, color: BRAND.muted, lineHeight: 1.65 }}>
                {description}
            </Typography>
        </Paper>
    );
}

function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);
    const fullText = lines.map((l) => l.cmd).join("\n");

    const handleCopy = () => {
        navigator.clipboard.writeText(fullText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 2 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Box sx={{ display: "flex", gap: 0.7 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                </Box>
                <Tooltip title={copied ? "Copied!" : "Copy commands"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span style={{ fontFamily: "monospace" }}>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
                {lines.map(({ cmd, comment }, i) => (
                    <Box key={i}>
                        {comment && <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: "rgba(255,255,255,0.3)", mb: 0.3 }}># {comment}</Typography>}
                        <Typography sx={{ fontFamily: "monospace", fontSize: { xs: 13, md: 14 }, color: BRAND.codeText }}>{cmd}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

function ExpandableExample({ title, icon, children }) {
    const [open, setOpen] = useState(false);
    return (
        <Box sx={{ border: `1px solid ${open ? BRAND.borderHover : BRAND.border}`, borderRadius: 2.5, overflow: "hidden", mb: 1.5 }}>
            <Box onClick={() => setOpen(!open)} sx={{ px: 2.5, py: 1.8, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", background: open ? BRAND.goldGlow : BRAND.surface }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                    {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
                    <Typography sx={{ color: BRAND.white, fontWeight: 600, fontSize: 14 }}>{title}</Typography>
                </Box>
                {open ? <ExpandLessIcon sx={{ color: BRAND.gold }} /> : <ExpandMoreIcon sx={{ color: BRAND.muted }} />}
            </Box>
            <Collapse in={open}>
                <Box sx={{ px: 2.5, py: 2, background: "rgba(0,0,0,0.25)", borderTop: `1px solid ${BRAND.border}` }}>{children}</Box>
            </Collapse>
        </Box>
    );
}

function ResourceLink({ href, label, sub }) {
    return (
        <Box component="a" href={href} target="_blank" rel="noreferrer" sx={{
            display: "flex", alignItems: "center", gap: 2, p: 2, mb: 1.2, textDecoration: "none",
            border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.surface, transition: "0.25s",
            "&:hover": { borderColor: BRAND.borderHover, boxShadow: `0 0 16px ${BRAND.goldGlow}` },
        }}>
            <LinkIcon sx={{ color: BRAND.gold, fontSize: 20, flexShrink: 0 }} />
            <Box><Typography sx={{ color: BRAND.gold, fontWeight: 700, fontSize: 14 }}>{label}</Typography>
                {sub && <Typography sx={{ color: BRAND.muted, fontSize: 12 }}>{sub}</Typography>}</Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function LinuxSSH() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const checkScroll = () => {
            if (!showScroll && window.pageYOffset > 400) setShowScroll(true);
            else if (showScroll && window.pageYOffset <= 400) setShowScroll(false);
        };
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, [showScroll]);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="Networking · Module 07"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        SSH & <span>Remote Access</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        SSH is the industry-standard gateway to remote Linux administration. Mastering secure tunnels and key-based authentication is a prerequisite for any Cloud or DevOps career.
                    </Typography>
                </Box>

                {/* WHAT IS SSH? */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SettingsEthernetIcon />} label="The Secure Shell Protocol" />
                    <Typography sx={{ color: BRAND.muted, lineHeight: 1.8, fontSize: { xs: 13, md: 15 } }}>
                        <strong style={{ color: BRAND.white }}>Secure Shell (SSH)</strong> is a cryptographic network protocol that operates as a client-server architecture. It provides a secure channel over an unsecured network, ensuring that commands sent to a remote server and the data received back are fully encrypted and protected from "man-in-the-middle" attacks.
                    </Typography>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* KEY CONCEPTS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SecurityIcon />} label="Security Pillars" />
                    <Grid container spacing={2}>
                        {[
                            { icon: <ShieldIcon />, title: "Encryption", description: "All traffic between the client and server is encrypted using symmetric and asymmetric algorithms." },
                            { icon: <VpnKeyIcon />, title: "Authentication", description: "Supports passwords, but 'Public Key Authentication' is the gold standard for secure, passwordless login." },
                            { icon: <RouterIcon />, title: "Port 22", description: "The default TCP port for SSH. Hardening often involves changing this to obscure the service from bots." },
                        ].map((card) => (
                            <Grid item xs={12} md={4} key={card.title}><ConceptCard {...card} /></Grid>
                        ))}
                    </Grid>
                </Box>

                {/* TERMINAL OPERATIONS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<TerminalIcon />} label="Common SSH Commands" />

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2 }}>🌐 Connecting to Servers</Typography>
                    <CodeBlock lines={[
                        { cmd: "$ ssh username@remote_ip",  comment: "Standard connection on port 22" },
                        { cmd: "$ ssh -p 2222 user@host",   comment: "Connect using a custom port" },
                        { cmd: "$ ssh user@192.168.1.10",   comment: "Access a machine on the local network" },
                    ]} />

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2, mt: 3 }}>🔑 Key Management</Typography>
                    <CodeBlock lines={[
                        { cmd: "$ ssh-keygen",                  comment: "Generate a new Ed25519 or RSA key pair" },
                        { cmd: "$ ssh-copy-id user@remote_ip",  comment: "Automated way to install your public key on the server" },
                        { cmd: "$ ssh user@remote_ip",          comment: "Log in securely without a password prompt" },
                    ]} />

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 2, mt: 4 }}>🔽 Advanced Configuration & Hardening</Typography>

                    <ExpandableExample title="Client-Side Shortcuts (~/.ssh/config)" icon={<SettingsEthernetIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2 }}>
                            Instead of typing long IP addresses, create aliases in your client config file:
                        </Typography>
                        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, p: 2.5, mb: 2 }}>
                            <Typography sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.gold, mb: 1 }}>Host prodev_server</Typography>
                            <Typography sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.white, ml: 2 }}>HostName 192.168.1.10</Typography>
                            <Typography sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.white, ml: 2 }}>User hazzan</Typography>
                            <Typography sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.white, ml: 2 }}>Port 2222</Typography>
                            <Typography sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.white, ml: 2 }}>IdentityFile ~/.ssh/id_rsa</Typography>
                        </Box>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>Now you can simply run: <code style={{ color: BRAND.goldBright }}>ssh prodev_server</code></Typography>
                    </ExpandableExample>

                    <ExpandableExample title="Server Hardening (/etc/ssh/sshd_config)" icon={<ShieldIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2 }}>
                            Edit these lines on your server to lock down remote access:
                        </Typography>
                        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, p: 2.5, mb: 1 }}>
                            <Typography sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.white }}>PermitRootLogin no</Typography>
                            <Typography sx={{ fontFamily: "monospace", fontSize: 13, color: "rgba(255,255,255,0.4)" }}># Disable password login entirely</Typography>
                            <Typography sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.white }}>PasswordAuthentication no</Typography>
                            <Typography sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.white }}>AllowUsers hazzan admin</Typography>
                        </Box>
                    </ExpandableExample>

                    <ExpandableExample title="Key Files Explained" icon={<VpnKeyIcon />}>
                        <Grid container spacing={2}>
                            {[
                                { file: "~/.ssh/id_rsa", desc: "Your PRIVATE key. NEVER share this file with anyone." },
                                { file: "~/.ssh/id_rsa.pub", desc: "Your PUBLIC key. This is safe to share and put on servers." },
                                { file: "~/.ssh/authorized_keys", desc: "List of public keys allowed to log into the server." },
                            ].map(item => (
                                <Grid item xs={12} key={item.file}>
                                    <Typography sx={{ color: BRAND.goldBright, fontFamily: "monospace", fontSize: 13 }}>{item.file}</Typography>
                                    <Typography sx={{ color: BRAND.muted, fontSize: 12 }}>{item.desc}</Typography>
                                </Grid>
                            ))}
                        </Grid>
                    </ExpandableExample>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* OUTCOMES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="Module Outcomes" />
                    <Grid container spacing={1.5}>
                        {[
                            "Connect to remote servers using the ssh command",
                            "Generate and manage SSH key pairs for better security",
                            "Install public keys on remote systems using ssh-copy-id",
                            "Create SSH client config files for easy server aliasing",
                            "Implement basic security hardening on the SSH daemon",
                            "Understand the difference between public and private keys",
                        ].map((item) => (
                            <Grid item xs={12} sm={6} key={item}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* RESOURCES */}
                <Box sx={{ mb: 4 }}>
                    <SectionTitle icon={<LinkIcon />} label="Deep Dive Resources" />
                    <ResourceLink href="https://linuxize.com/post/how-to-use-ssh-to-connect-to-a-remote-server/" label="SSH for Beginners" sub="Comprehensive guide to basic and advanced SSH usage." />
                    <ResourceLink href="https://www.ssh.com/academy/ssh" label="SSH Academy" sub="Educational portal focused on cryptographic security and SSH standards." />
                    <ResourceLink href="https://wiki.archlinux.org/title/Secure_Shell" label="Arch Wiki: SSH" sub="Detailed documentation covering edge cases and advanced setups." />
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Junior Dev Tip:</strong> If you are locked out or experiencing issues, use the verbose flag: <code style={{ color: BRAND.goldBright }}>ssh -vvv user@host</code>. This debug mode will show you exactly where the handshake is failing!
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
